import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SignInHeader from "../auth/SigninHeader";
import Navigation from "../layout/Navigation";


const ContactTypes = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [authenticated, setAuthenticated] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [group, setGroup] = useState("");
    const [type, setType] = useState("");
    const [subtype, setSubType] = useState("");
    const [category, setCategory] = useState("");
    const [selectedG6Type, setSelectedG6Type] = useState("");
    const [contactTypes, setContactTypes] = useState([]);
    const [loadingContactTypes, setLoadingContactTypes] = useState(false);
    const [user, setUser] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [backendError, setBackendError] = useState("");
    const [selectedContactType, setSelectedContactType] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false); // State for Next button loading

    useEffect(() => {
        const token = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");
        const savedTheme = localStorage.getItem("theme");
        const prefersDark =
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches;
        
        setIsDarkMode(savedTheme === "dark" || (!savedTheme && prefersDark));
        
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error('Error parsing stored user:', e);
                localStorage.removeItem('user');
            }
        }

        const { selectedTemplate, group, type, subtype, category, selectedG6Type } = location.state || {};
        setSelectedTemplate(selectedTemplate || localStorage.getItem("selectedTemplate") || "AA - Received");
        setGroup(group || localStorage.getItem("group") || "Building");
        setType(type || localStorage.getItem("type") || "Residential");
        setSubType(subtype || localStorage.getItem("subtype") || "NA");
        setCategory(category || localStorage.getItem("category") || "NA");
        setSelectedG6Type(selectedG6Type || localStorage.getItem("selectedG6Type") || "");

        const isAuthenticated = !!token;
        setAuthenticated(isAuthenticated);

        if (!isAuthenticated) {
            setErrorMessage("Please sign in to access this page.");
            navigate("/signin");
            return;
        }
    }, [navigate, location.state]);

    useEffect(() => {
        if (authenticated) {
            const fetchContactTypes = async () => {
                try {
                    setLoadingContactTypes(true);
                    setBackendError("");
                    
                    const token = localStorage.getItem("token");
                    if (!token) {
                        setBackendError("Authentication token missing. Please sign in.");
                        navigate("/signin");
                        return;
                    }

                    const response = await axios.get("http://localhost:5000/api/get-contact-types", {
                        headers: { Authorization: token },
                    });

                    console.log('Contact types response:', response.data);

                    if (response.data.success) {
                        const contactTypesString = response.data.contactTypes || response.data.ContactTypesList || "";
                        if (contactTypesString && contactTypesString.includes("No Contact Types found")) {
                            setContactTypes([]);
                        } else {
                            const types = contactTypesString
                                .split("\n")
                                .filter(line => line.trim().startsWith("- "))
                                .map(line => line.replace("- ", "").trim());
                            setContactTypes(types);
                        }
                    } else {
                        setBackendError(response.data.message || "Failed to retrieve contact types.");
                        setContactTypes([]);
                    }
                } catch (error) {
                    console.error("Error fetching contact types from backend:", error);
                    
                    if (error.response?.status === 401) {
                        setBackendError("Your session has expired. Please sign in again.");
                        localStorage.removeItem('token');
                        navigate("/signin");
                    } else {
                        setBackendError("Failed to retrieve contact types. Please check console for details.");
                        setContactTypes([]);
                    }
                } finally {
                    setLoadingContactTypes(false);
                }
            };
            fetchContactTypes();
        }
    }, [authenticated, navigate]);

    const toggleDarkMode = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        localStorage.setItem("theme", newMode ? "dark" : "light");
    };

    const handleTemplateClick = () => {
        if (selectedTemplate) {
            navigate("/template-viewer", { state: { selectedTemplate } });
        }
    };

    const handleRecordClick = () => {
        if (group && type && subtype && category) {
            navigate("/record-types", {
                state: { group, type, subtype, category },
            });
        }
    };

    const handleInspectionTypeClick = () => {
        if (selectedG6Type) {
            navigate("/inspection-type", {
                state: { selectedTemplate, group, type, subtype, category, selectedG6Type },
            });
        }
    };

    const handleNextClick = async () => {
        if (!selectedContactType) {
            setBackendError("Please select a contact type before proceeding.");
            return;
        }

        setIsSubmitting(true);
        setBackendError("");

        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/save-contact-type`,
                { contactType: selectedContactType },
                { headers: { Authorization: token } }
            );

            localStorage.setItem("selectedContactType", selectedContactType);

            navigate("/summary", { 
                state: {
                    selectedTemplate,
                    group,
                    type,
                    subtype,
                    category,
                    selectedG6Type,
                    selectedContactType
                }
            });
        } catch (error) {
            console.error("Error saving contact type:", error);
            const message = error.response?.data?.message || "Failed to save your selection. Please try again.";
            setBackendError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`flex flex-col min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-main-dark text-white" : "bg-gray-100 text-gray-900"}`}>
            <SignInHeader
                user={user}
                isLoggedIn={authenticated}
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
            />
            <Navigation
                activeTab="My Apps"
                setActiveTab={() => {}}
                isDarkMode={isDarkMode}
                toggleDarkMode={toggleDarkMode}
            />

            <div className="flex flex-col items-center pt-[140px] pb-10 px-5 flex-grow">
                <div className="self-start ml-6 mt-4 mb-3 px-3 py-2">
                    <button onClick={handleTemplateClick} className="bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800" disabled={!selectedTemplate}>
                        {selectedTemplate || "No Template Selected"} |
                    </button>
                    <button onClick={handleRecordClick} className="ml-1 bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800" disabled={!group || !type || !subtype || !category}>
                        {`${group}/${type}/${subtype}/${category}`} |
                    </button>
                    <button onClick={handleInspectionTypeClick} className="ml-1 bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800" disabled={!selectedG6Type}>
                        {selectedG6Type || "No Inspection Type Selected"}
                    </button>
                </div>

                {backendError && (
                    <div className="w-full max-w-md mb-4 p-3 rounded bg-red-50 border border-red-200">
                        <div className="flex items-center justify-between">
                            <p className="text-red-600 text-sm">{backendError}</p>
                            <button onClick={() => setBackendError("")} className="text-red-400 hover:text-red-600 text-lg">
                                ×
                            </button>
                        </div>
                    </div>
                )}

                <div className="w-full max-w-md">
                    <div className="mb-6">
                        <div className="relative">
                            <div className="bg-[#4a4a68] lg shadow-md">
                                <div className="px-4 py-3 bg-[#4a4a68] text-white text-sm font-medium rounded-t-lg">
                                    Select contact type...
                                </div>
                                <div className="bg-[#4a4a68] px-4 py-3 rounded-b-lg">
                                    {loadingContactTypes ? (
                                        <div className="text-center text-gray-300 py-2">Loading contact types...</div>
                                    ) : contactTypes.length > 0 ? (
                                        <select
                                            className="w-full bg-[#e8e8f0] text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            value={selectedContactType}
                                            onChange={(e) => {
                                                setSelectedContactType(e.target.value);
                                                setBackendError("");
                                            }}
                                        >
                                            <option value="" disabled>Select a contact type</option>
                                            {contactTypes.map((contactType, index) => (
                                                <option key={index} value={contactType}>
                                                    {contactType}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="text-center text-gray-300 py-2">
                                            No contact types found for the selected record type.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="absolute right-0 top-0 bg-[#2a2a48] text-white px-4 py-4 rounded-r-lg">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <button
                            onClick={handleNextClick}
                            className={`font-medium px-8 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                selectedContactType && !loadingContactTypes && !isSubmitting
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                                    : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            }`}
                            disabled={!selectedContactType || loadingContactTypes || isSubmitting}
                        >
                            {isSubmitting ? 'Saving...' : (loadingContactTypes ? 'Loading...' : 'Next')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactTypes;